import React, { useState, useEffect, useCallback } from "react"
import {
  Drawer,
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Empty,
  Flex,
  Typography,
  Timeline,
  Badge,
  Collapse,
} from "antd"
import {
  ReloadOutlined,
  HistoryOutlined,
  UserOutlined,
  EditOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  GlobalOutlined,
} from "@ant-design/icons"
import { getAuditLogsByFileNumberApi } from "helpers/backend_helper"

const { Text, Title } = Typography

const STATE_CONFIG = {
  2: { label: "ویرایش", color: "warning", icon: <EditOutlined /> },
  4: { label: "افزودن", color: "success", icon: <PlusCircleOutlined /> },
  8: { label: "حذف", color: "error", icon: <DeleteOutlined /> },
}

const getStateConfig = state => STATE_CONFIG[state] ?? { label: String(state), color: "default", icon: null }

const ChangesTable = ({ changes }) => {
  if (!changes?.length) return <Text type="secondary">تغییری ثبت نشده</Text>

  return (
    <Table
      size="small"
      pagination={false}
      dataSource={changes}
      rowKey={(_, i) => i}
      columns={[
        {
          title: "فیلد",
          dataIndex: "propertyName",
          key: "propertyName",
          width: 140,
          render: val => (
            <Text strong style={{ fontSize: 12 }}>
              {val}
            </Text>
          ),
        },
        {
          title: "مقدار قبلی",
          dataIndex: "oldValue",
          key: "oldValue",
          render: val => (
            <Tag
              color="red"
              style={{ fontSize: 11, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {val || "—"}
            </Tag>
          ),
        },
        {
          title: "",
          key: "arrow",
          width: 30,
          align: "center",
          render: () => <ArrowRightOutlined style={{ color: "#8c8c8c", fontSize: 10 }} />,
        },
        {
          title: "مقدار جدید",
          dataIndex: "newValue",
          key: "newValue",
          render: val => (
            <Tag
              color="green"
              style={{ fontSize: 11, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {val || "—"}
            </Tag>
          ),
        },
      ]}
      style={{ background: "#fafafa", borderRadius: 8 }}
    />
  )
}

const auditColumns = [
  {
    title: "تاریخ",
    key: "solarTimeStamp",
    dataIndex: "solarTimeStamp",
    width: 130,
    render: val => (
      <Tag style={{ borderRadius: 6, fontSize: 11, fontFamily: "tahoma" }}>
        📅 {val}
      </Tag>
    ),
  },
  {
    title: "کاربر",
    key: "userFullName",
    dataIndex: "userFullName",
    width: 130,
    render: val => (
      <Space size={4}>
        <UserOutlined style={{ color: "#1890ff" }} />
        <Text style={{ fontSize: 12 }}>{val || "—"}</Text>
      </Space>
    ),
  },
  {
    title: "موجودیت",
    key: "entityName",
    dataIndex: "entityName",
    width: 120,
    render: val => (
      <Tag color="geekblue" style={{ borderRadius: 6, fontSize: 11 }}>
        {val}
      </Tag>
    ),
  },
  {
    title: "عملیات",
    key: "state",
    dataIndex: "state",
    width: 90,
    align: "center",
    render: val => {
      const cfg = getStateConfig(val)
      return (
        <Tag icon={cfg.icon} color={cfg.color} style={{ borderRadius: 6, fontSize: 11 }}>
          {cfg.label}
        </Tag>
      )
    },
  },
  {
    title: "IP",
    key: "ip",
    dataIndex: "ip",
    width: 120,
    render: val => (
      <Space size={4}>
        <GlobalOutlined style={{ color: "#8c8c8c", fontSize: 10 }} />
        <Text type="secondary" style={{ fontSize: 11, fontFamily: "tahoma" }}>
          {val || "—"}
        </Text>
      </Space>
    ),
  },
  {
    title: "تعداد تغییر",
    key: "changes",
    dataIndex: "changes",
    width: 90,
    align: "center",
    render: changes => (
      <Badge
        count={changes?.length ?? 0}
        style={{ backgroundColor: changes?.length ? "#1890ff" : "#d9d9d9" }}
        overflowCount={99}
      />
    ),
  },
]

const AuditLogDrawer = ({ open, onClose, fileNumber }) => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchLogs = useCallback(async () => {
    if (!fileNumber) return
    setLoading(true)
    try {
      const res = await getAuditLogsByFileNumberApi(fileNumber)
      setLogs(Array.isArray(res) ? res : [])
    } catch {
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [fileNumber])

  useEffect(() => {
    if (open && fileNumber) fetchLogs()
    if (!open) setLogs([])
  }, [open, fileNumber])

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <HistoryOutlined style={{ color: "#722ed1" }} />
          <span>تاریخچه تغییرات پرونده</span>
          <Tag color="purple" style={{ marginRight: 0 }}>
            {fileNumber}
          </Tag>
          <Badge
            count={logs.length}
            style={{ backgroundColor: "#722ed1" }}
            overflowCount={999}
          />
        </Flex>
      }
      placement="bottom"
      height="70vh"
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchLogs}
          disabled={loading}
        >
          بروزرسانی
        </Button>
      }
    >
      <Table
        loading={loading}
        size="small"
        columns={auditColumns}
        dataSource={logs}
        rowKey={record => record.id}
        rowClassName={(_, i) => (i % 2 ? "odd" : "even")}
        scroll={{ x: 700 }}
        expandable={{
          expandedRowRender: record => (
            <div style={{ padding: "8px 16px" }}>
              <ChangesTable changes={record.changes} />
            </div>
          ),
          rowExpandable: record => record.changes?.length > 0,
        }}
        pagination={{
          pageSize: 15,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} از ${total} رکورد`,
        }}
        locale={{
          emptyText: (
            <Empty
              description="تاریخچه‌ای برای این پرونده یافت نشد"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
    </Drawer>
  )
}

export default AuditLogDrawer
